import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { adminApi } from "@/lib/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertOctagon, ArrowLeft, Save } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const DisputesList = () => {
  const [status, setStatus] = useState("open");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/disputes?status=${status}`);
      setRows(r.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  return (
    <div className="p-8">
      <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900"><AlertOctagon className="w-6 h-6 inline mr-2"/> Disputes & claims</h1>
          <p className="text-sm text-slate-500 mt-1">Every dispute raised on a delivery — open ones first.</p>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="filter-status" className="w-[180px]"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
            <SelectItem value="all">All</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading && <div className="text-sm text-slate-500">Loading…</div>}
      {!loading && rows.length === 0 && <div className="rounded-2xl bg-white border border-slate-200 p-8 text-center text-sm text-slate-500">No disputes.</div>}
      <div className="space-y-2">
        {rows.map(dp => (
          <Link key={dp.id} to={`/admin/disputes/${dp.id}`} data-testid={`disp-row-${dp.id}`} className="block rounded-2xl bg-white border border-slate-200 hover:border-slate-400 p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Badge className={`text-[10px] ${dp.status === "open" ? "bg-red-100 text-red-700" : "bg-slate-200 text-slate-700"}`}>{dp.status?.toUpperCase()}</Badge>
                  <div className="font-mono text-xs text-slate-500">{(dp.parcel?.id || dp.delivery_id).slice(0, 8)}</div>
                  <span className="text-xs text-slate-500">{new Date(dp.created_at).toLocaleString()}</span>
                </div>
                <div className="mt-1 text-sm font-medium text-slate-900">{dp.reason}</div>
                <div className="text-xs text-slate-500 truncate">{dp.details}</div>
                <div className="text-xs text-slate-500 mt-1">
                  <b>Sender:</b> {dp.sender?.name} · <b>Carrier:</b> {dp.carrier?.name} · raised by <span className="uppercase">{dp.raised_role}</span>
                </div>
              </div>
              {dp.status === "resolved" && dp.resolution_action && (
                <Badge className="text-[10px] bg-emerald-100 text-emerald-700">{dp.resolution_action}</Badge>
              )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export const DisputeDetail = () => {
  const { did } = useParams();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState("");
  const [action, setAction] = useState("close_no_action");
  const [refundAmt, setRefundAmt] = useState("");
  const [compAmt, setCompAmt] = useState("");
  const [resolveOpen, setResolveOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/disputes/${did}`);
      setD(r.data);
      setNote(r.data.dispute?.admin_note || "");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [did]);

  const saveNote = async () => {
    try {
      await adminApi.post(`/disputes/${did}/note`, { note });
      toast.success("Note saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const resolve = async () => {
    setBusy(true);
    try {
      const body = { action, note };
      if (action === "refund_partial") body.refund_amount = Number(refundAmt);
      if (action === "protection_payout") body.compensation_amount = Number(compAmt);
      await adminApi.post(`/disputes/${did}/resolve`, body);
      toast.success("Dispute resolved");
      setResolveOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (loading || !d) return <div className="p-8 text-sm text-slate-500">Loading…</div>;
  const { dispute, parcel, sender, carrier, delivery } = d;
  const isOpen = dispute.status === "open";

  return (
    <div className="p-8">
      <Link to="/admin/disputes" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft className="w-3 h-3"/> Back to disputes</Link>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-xl font-bold text-slate-900">Dispute {dispute.id.slice(0, 8)}</h1>
            <Badge className={`text-[10px] ${isOpen ? "bg-red-100 text-red-700" : "bg-slate-200 text-slate-700"}`} data-testid="disp-status-badge">{dispute.status?.toUpperCase()}</Badge>
          </div>
          <div className="text-xs text-slate-500 mt-1">Raised {new Date(dispute.created_at).toLocaleString()} by <b className="uppercase">{dispute.raised_role}</b></div>
        </div>
        {isOpen && <Button data-testid="btn-open-resolve" onClick={() => setResolveOpen(true)} className="bg-slate-900 hover:bg-slate-800">Resolve dispute</Button>}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card title="What was reported">
          <p className="text-sm text-slate-900 font-medium">{dispute.reason}</p>
          <p className="text-xs text-slate-500 mt-1">{dispute.details || <em>No further details</em>}</p>
        </Card>
        <Card title="Parcel">
          {parcel ? (
            <>
              <Row label="Parcel" value={<Link to={`/admin/parcels/${parcel.id}`} className="font-mono text-xs underline">{parcel.id.slice(0, 8)}</Link>}/>
              <Row label="Route" value={`${parcel.pickup_location} → ${parcel.drop_location}`}/>
              <Row label="Declared value" value={parcel.declared_value ? `₹${parcel.declared_value.toFixed(2)}` : "—"}/>
              <Row label="Protection purchased" value={d.protection_purchased ? "YES" : "NO"}/>
              <Row label="Delivery status" value={delivery?.status?.toUpperCase()}/>
            </>
          ) : <em className="text-slate-400 text-xs">Not available</em>}
        </Card>
        <Card title="Parties">
          <div className="text-xs">
            <div><b>Sender:</b> <Link to={`/admin/users/${sender?.id}`} className="underline">{sender?.name}</Link> · {sender?.phone}</div>
            <div className="mt-1"><b>Carrier:</b> <Link to={`/admin/users/${carrier?.id}`} className="underline">{carrier?.name}</Link> · {carrier?.phone}</div>
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <Card title="Pickup photo (evidence)">
          {d.pickup_photo_url
            ? <img src={`${BACKEND}${d.pickup_photo_url}`} alt="Pickup" className="rounded-lg max-h-[240px]" data-testid="disp-pickup-photo"/>
            : <div className="text-xs text-slate-400 italic">No pickup photo.</div>}
        </Card>
        <Card title="Internal notes (admin only)">
          <Textarea data-testid="disp-note" value={note} onChange={(e) => setNote(e.target.value)} rows={5} placeholder="Investigation notes — not visible to users."/>
          <Button data-testid="btn-save-note" onClick={saveNote} variant="outline" className="mt-2"><Save className="w-3 h-3 mr-1"/> Save notes</Button>
        </Card>
      </div>

      {!isOpen && (
        <Card title="Resolution">
          <Row label="Action" value={<b>{dispute.resolution_action}</b>}/>
          {dispute.refund_amount && <Row label="Refund amount" value={`₹${dispute.refund_amount}`}/>}
          {dispute.compensation_amount && <Row label="Protection payout" value={`₹${dispute.compensation_amount}`}/>}
          <Row label="Resolved at" value={dispute.resolved_at ? new Date(dispute.resolved_at).toLocaleString() : "—"}/>
          <Row label="Resolver" value={dispute.resolved_by || "—"}/>
          <div className="mt-3 rounded-lg bg-slate-50 p-3 text-sm">{dispute.resolution_note}</div>
        </Card>
      )}

      <Dialog open={resolveOpen} onOpenChange={setResolveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve dispute</DialogTitle>
            <DialogDescription>Pick a resolution action. Every option is written to the audit log.</DialogDescription>
          </DialogHeader>
          <label className="text-[11px] uppercase tracking-wider text-slate-500">Action</label>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger data-testid="resolve-action"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="refund_full">Refund sender in full</SelectItem>
              <SelectItem value="refund_partial">Partial refund to sender</SelectItem>
              <SelectItem value="release_carrier">Release payment to carrier</SelectItem>
              <SelectItem value="protection_payout">CarryIt Protection payout (platform-funded)</SelectItem>
              <SelectItem value="close_no_action">Close with no financial action</SelectItem>
            </SelectContent>
          </Select>
          {action === "refund_partial" && (
            <div className="mt-2">
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Refund amount (₹)</label>
              <Input data-testid="refund-amount" type="number" step="0.01" value={refundAmt} onChange={(e) => setRefundAmt(e.target.value)} placeholder="e.g. 250.00"/>
            </div>
          )}
          {action === "protection_payout" && (
            <div className="mt-2">
              <label className="text-[11px] uppercase tracking-wider text-slate-500">Compensation amount (₹)</label>
              <Input data-testid="comp-amount" type="number" step="0.01" value={compAmt} onChange={(e) => setCompAmt(e.target.value)} placeholder="e.g. 1000.00"/>
            </div>
          )}
          <label className="text-[11px] uppercase tracking-wider text-slate-500 mt-2">Resolution note (required)</label>
          <Textarea data-testid="resolve-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Explain the decision."/>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setResolveOpen(false)}>Cancel</Button>
            <Button data-testid="btn-confirm-resolve" disabled={!note.trim() || busy} onClick={resolve} className="bg-slate-900 hover:bg-slate-800">Confirm resolution</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Card = ({ title, children }) => (
  <div className="rounded-2xl bg-white border border-slate-200 p-4">
    <div className="text-xs uppercase tracking-wider text-slate-500 mb-2 font-medium">{title}</div>
    <div className="space-y-1">{children}</div>
  </div>
);
const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-1 text-sm gap-2">
    <span className="text-slate-500 text-xs">{label}</span>
    <span className="text-slate-900 text-right text-xs">{value ?? <em className="text-slate-400">—</em>}</span>
  </div>
);

export default DisputesList;
